import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../commons/axiosReq";
import { siteUrl } from "../../config/urls";
import ArticleRichEditor from "../../components/MainAdmin/Articles/ArticleRichEditor";
import keywordPages from "../../data/keywordPages.json";
import "../../components/MainAdmin/Articles/Articles.css";
import "./MainAdminKeywordPages.css";

const MAX_CONTENT_LENGTH = 500000;
const formFromPage = (page = {}) => Object.fromEntries(["description", "descriptionEn", "metaTitle", "metaTitleEn", "metaDescription", "metaDescriptionEn"].map(name => [name, page?.[name] || ""]));

export default function MainAdminKeywordPages({ countryPages = false }) {
  const [pages, setPages] = useState(countryPages ? [] : keywordPages);
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState(formFromPage);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [pendingFa, setPendingFa] = useState(false);
  const [pendingEn, setPendingEn] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const page = pages.find(item => item.slug === selected);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const result = await apiFetch(countryPages ? "KeywordPage?group=countries" : "KeywordPage");
    if (result.ok && Array.isArray(result.data) && (!countryPages || result.data.every(item => item.slug?.startsWith("country-")))) {
      const available = countryPages ? result.data : keywordPages.map(definition => ({ ...result.data.find(item => item.slug === definition.slug), ...definition }));
      setPages(available);
      const first = available[0];
      setSelected(first?.slug || "");
      setForm(formFromPage(first));
      setLoaded(true); setDirty(false);
    } else {
      setLoaded(false);
      if (!countryPages) { setPages(keywordPages); setSelected(keywordPages[0].slug); }
      setError("دریافت توضیحات از سرور ناموفق بود. برای جلوگیری از بازنویسی محتوای قبلی، پس از اتصال دوباره تلاش کنید.");
    }
    setLoading(false);
  }, [countryPages]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const warn = event => { if (dirty) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  function select(slug) {
    if (slug === selected) return;
    if (dirty && !window.confirm("تغییرات ذخیره نشده‌اند. از تغییر صفحه مطمئن هستید؟")) return;
    const next = pages.find(item => item.slug === slug);
    setSelected(slug);
    setForm(formFromPage(next));
    setDirty(false); setMessage(""); if (loaded) setError("");
  }
  async function save(event) {
    event.preventDefault();
    if (!loaded) { setError("اطلاعات صفحه هنوز از سرور دریافت نشده است. ابتدا روی «تلاش دوباره» بزنید."); return; }
    if (pendingFa || pendingEn) { setError("ابتدا بارگذاری و درج تصویر یا ویدیو را کامل کنید."); return; }
    if (form.description.length > MAX_CONTENT_LENGTH || form.descriptionEn.length > MAX_CONTENT_LENGTH) { setError("حداکثر اندازه متن هر زبان ۵۰۰٬۰۰۰ نویسه است."); return; }
    if ([form.metaTitle, form.metaTitleEn].some(value => value.length > 200) || [form.metaDescription, form.metaDescriptionEn].some(value => value.length > 500)) { setError("عنوان متا حداکثر ۲۰۰ و توضیحات متا حداکثر ۵۰۰ نویسه است."); return; }
    setSaving(true); setError(""); setMessage("");
    const result = await apiFetch(`KeywordPage/${selected}`, "put", form);
    if (result.ok) {
      setPages(current => current.map(item => item.slug === selected ? result.data : item));
      setForm(formFromPage(result.data));
      setDirty(false); setMessage("محتوا و تنظیمات متا ذخیره شد.");
    } else setError(result.error || "ذخیره توضیحات ناموفق بود. دوباره تلاش کنید.");
    setSaving(false);
  }
  return <section className="admin-keywords" dir="rtl">
    <h1>{countryPages ? "صفحات کشورها" : "صفحات کلمات کلیدی"}</h1>
    <p>{countryPages ? "هر کشور یک صفحهٔ مستقل دارد. متن معرفی، شرایط پذیرش، هزینه‌ها و بورسیه‌ها را اینجا بنویسید؛ دانشگاه‌های همان کشور زیر متن نمایش داده می‌شوند." : "این صفحات از هدر سایت در دسترس هستند. توضیحات هر موضوع را اینجا وارد کنید؛ متن خالی را می‌توانید بعداً تکمیل کنید."}</p>
    {error && <div role="alert" className="admin-keywords__error">{error}{!loaded && <button onClick={load} disabled={loading}>تلاش دوباره</button>}</div>}
    {message && <p role="status" className="admin-keywords__success">{message}</p>}
    {loading ? <p role="status">در حال دریافت صفحات…</p> : page && <form onSubmit={save}>
      <label htmlFor="keyword-page">{countryPages ? "کشور" : "کلمه کلیدی"}</label>
      <select id="keyword-page" value={selected} onChange={event => select(event.target.value)} disabled={saving || pendingFa || pendingEn}>{pages.map(item => <option key={item.slug} value={item.slug}>{item.title}</option>)}</select>
      <a href={siteUrl(countryPages ? `/countries/apply-${selected.replace(/^country-/, "")}` : `/guides/${selected}`)} target="_blank" rel="noreferrer">مشاهده صفحه عمومی ↗</a>
      <section className="admin-keywords__seo" aria-label="تنظیمات متا">
        <h2>تنظیمات متا (اختیاری)</h2>
        <p>اگر خالی باشند، عنوان و توضیحات به‌صورت خودکار از محتوای صفحه ساخته می‌شوند.</p>
        {[["metaTitle", "عنوان متا فارسی (Meta Title)", 200], ["metaDescription", "توضیحات متا فارسی (Meta Description)", 500], ["metaTitleEn", "عنوان متا انگلیسی (Meta Title)", 200], ["metaDescriptionEn", "توضیحات متا انگلیسی (Meta Description)", 500]].map(([name, label, maxLength]) => <label key={name} htmlFor={`keyword-${name}`}>
          <span>{label}</span>
          {name.includes("Description") ? <textarea id={`keyword-${name}`} name={name} rows={3} dir={name.endsWith("En") ? "ltr" : "rtl"} value={form[name]} maxLength={maxLength} disabled={saving || !loaded} onChange={event => { setForm(current => ({ ...current, [name]: event.target.value })); setDirty(true); setMessage(""); }} /> : <input id={`keyword-${name}`} name={name} dir={name.endsWith("En") ? "ltr" : "rtl"} value={form[name]} maxLength={maxLength} disabled={saving || !loaded} onChange={event => { setForm(current => ({ ...current, [name]: event.target.value })); setDirty(true); setMessage(""); }} />}
          <small>{form[name].length.toLocaleString("fa-IR")} / {maxLength.toLocaleString("fa-IR")} نویسه</small>
        </label>)}
      </section>
      <section aria-label="توضیحات فارسی" className="article-main-column" dir="rtl">
        <h2>توضیحات فارسی</h2>
        <ArticleRichEditor key={`${selected}-fa`} value={form.description} preferSource disabled={saving || !loaded} onPendingChange={setPendingFa} onChange={value => { setForm(current => ({ ...current, description: value })); setDirty(true); setMessage(""); }} />
      </section>
      <section aria-label="توضیحات انگلیسی" className="article-main-column" dir="ltr">
        <h2>توضیحات انگلیسی (اختیاری)</h2>
        <ArticleRichEditor key={`${selected}-en`} value={form.descriptionEn} preferSource disabled={saving || !loaded} onPendingChange={setPendingEn} onChange={value => { setForm(current => ({ ...current, descriptionEn: value })); setDirty(true); setMessage(""); }} />
      </section>
      <p>تغییرات پس از ذخیره در صفحه عمومی منتشر می‌شوند.</p>
      <button type="submit" disabled={saving || !dirty || !loaded || pendingFa || pendingEn}>{saving ? "در حال ذخیره…" : "ذخیره محتوا و متا"}</button>
    </form>}
  </section>;
}
